import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useRealm } from '../context/RealmContext';
import { useSpace } from '../context/SpaceContext';
import { dashboardCache } from '../services/dashboardCache';

import { DirectoryList } from '../components/ui/directory-list';

import PricingModal from '../components/PricingModal';
import { billingService } from '../services/billing';
import { useTier } from '../contexts/TierContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { extractErrorMessage } from '../utils/errorUtils';
import { useActivityTimeline } from '../hooks/useActivityTimeline';
import { useQueryClient } from 'react-query';
import {
  useSpaceMemoryCount,
  spaceMemoryCountQueryKey,
} from '../hooks/useSpaceMemoryCount';

import {
  BarChart3,
  TrendingUp,
  Search,
  Plus,
  Clock,
  Settings,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  Trophy,
  Trash2,
  Layers,
} from 'lucide-react';
import AnalyticsPage from './AnalyticsPage';
import MonitoringPage from './MonitoringPage';
import IntegrationsPage from './IntegrationsPage';
import PortalButton from '../components/PortalButton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { weeklyStatsPatchForDashboard } from '../utils/dashboardWeeklyStats';

interface DashboardStats {
  totalSpaces: number;
  /** null when no space selected (same as memory count card). */
  storageThisWeek: number | null;
  retrievalThisWeek: number | null;
  totalMemories: number;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { enterWorkspace } = useRealm();
  const {
    availableSpaces,
    totalSpaces,
    isLoadingSpaces,
    spacesError,
    refreshSpaces,
    currentSpace,
  } = useSpace();
  const spaceMemoryCount = useSpaceMemoryCount(currentSpace?.space_id);
  const queryClient = useQueryClient();
  const { tierInfo, refreshTierInfo: forceRefreshTierInfo, getResourceLimits } = useTier();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { recentActivities: activityTimelineActivities, isLoading: isLoadingActivities } = useActivityTimeline();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [_shouldShowUpgradePrompt, setShouldShowUpgradePrompt] = useState(false);
  const [_upgradeContext, setUpgradeContext] = useState<string>('');
  const [stats, setStats] = useState<DashboardStats>({
    totalSpaces: 0, // Will be updated from SpaceContext
    storageThisWeek: null,
    retrievalThisWeek: null,
    totalMemories: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const activeView = searchParams.get('view') || 'overview';
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showAdvancedTools, setShowAdvancedTools] = useState(false);
  const [showRecentActivityExpanded, setShowRecentActivityExpanded] = useState(false);
  const [showPostLoginHint, setShowPostLoginHint] = useState(false);

  // One-time post-login hint when redirected from home
  useEffect(() => {
    try {
      if (sessionStorage.getItem('memara_show_dashboard_hint') === 'true') {
        sessionStorage.removeItem('memara_show_dashboard_hint');
        setShowPostLoginHint(true);
      }
    } catch (_) {}
  }, []);

  // Ref guards to prevent duplicate loads and concurrent API calls
  const isLoadingRef = useRef(false);
  useEffect(() => {
    return () => {
      isLoadingRef.current = false;
    };
  }, []);
  const hasLoadedRef = useRef(false);
  /** When true, a load was skipped while another was in flight; run one follow-up after the active load finishes. */
  const pendingDashboardLoadRef = useRef(false);
  /** Last space id used for weekly metrics (updated in loadDashboardData finally). */
  const previousSpaceIdRef = useRef<string | null>(null);
  const previousSpacesLengthRef = useRef<number>(-1); // Track previous spaces length to detect actual changes
  const loadedWithZeroSpacesRef = useRef(false); // Track if we initially loaded with 0 spaces (timeout case)
  const forceRefreshTierInfoRef = useRef(forceRefreshTierInfo);
  forceRefreshTierInfoRef.current = forceRefreshTierInfo;
  const getResourceLimitsRef = useRef(getResourceLimits);
  getResourceLimitsRef.current = getResourceLimits;

  /** Fresh ids after awaits (avoids stale closure when spaces/currentSpace update during load). */
  const currentSpaceIdRef = useRef<string | null>(null);
  const firstAvailableSpaceIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentSpaceIdRef.current = currentSpace?.space_id ?? null;
    firstAvailableSpaceIdRef.current = availableSpaces[0]?.space_id ?? null;
  }, [currentSpace?.space_id, availableSpaces]);

  // Add loading timeout to prevent dashboard from getting stuck.
  // Keep the id in a ref. State updates here re-rendered the page, and the
  // cleanup closed over the initial null so unmount never cleared the timer.
  useEffect(() => {
    loadingTimeoutRef.current = setTimeout(() => {
      console.log('⏰ Dashboard loading timeout reached, showing content with fallback data');
      setIsLoading(false);
    }, 15000); // 15 second timeout

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, []);

  // Clear loading timeout when loading completes
  useEffect(() => {
    if (!isLoading && loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, [isLoading]);

  useEffect(() => {
    setStats((prev) => ({ ...prev, totalMemories: spaceMemoryCount.count }));
  }, [spaceMemoryCount.count]);

  const loadDashboardData = useCallback(
    async (forceRefresh = false, spaceIdForWeekly?: string | null) => {
      // Prevent concurrent loads; defer one retry so weekly metrics are not stranded (see pendingDashboardLoadRef).
      if (isLoadingRef.current && !forceRefresh) {
        console.log('⏸️ Dashboard data already loading, will retry after current load finishes');
        pendingDashboardLoadRef.current = true;
        return;
      }

      let effectiveWeeklySpaceId: string | null = null;

      try {
        isLoadingRef.current = true;
        setIsLoading(true);

        // Invalidate cache if force refresh is requested
        if (forceRefresh) {
          dashboardCache.invalidateCache();
          console.log('🔄 Cache invalidated for dashboard refresh');
          if (currentSpace?.space_id) {
            queryClient.invalidateQueries(spaceMemoryCountQueryKey(currentSpace.space_id));
          }
        }

        let storageThisWeek: number | null = null;
        let retrievalThisWeek: number | null = null;
        try {
          await dashboardCache.getDashboardMetrics(availableSpaces);
          effectiveWeeklySpaceId =
            spaceIdForWeekly ??
            currentSpaceIdRef.current ??
            firstAvailableSpaceIdRef.current ??
            null;
          if (effectiveWeeklySpaceId) {
            const weekly = await dashboardCache.getWeeklyMemoryActivity(effectiveWeeklySpaceId);
            storageThisWeek = weekly.storageThisWeek;
            retrievalThisWeek = weekly.retrievalThisWeek;
          }
        } catch (metricsError) {
          console.warn('Failed to load dashboard metrics:', metricsError);
          effectiveWeeklySpaceId =
            effectiveWeeklySpaceId ??
            spaceIdForWeekly ??
            currentSpaceIdRef.current ??
            firstAvailableSpaceIdRef.current ??
            null;
          storageThisWeek = effectiveWeeklySpaceId ? 0 : null;
          retrievalThisWeek = effectiveWeeklySpaceId ? 0 : null;
        }

        const hasAnySpace =
          availableSpaces.length > 0 ||
          !!currentSpace ||
          !!currentSpaceIdRef.current ||
          !!firstAvailableSpaceIdRef.current;

        const weeklyPatch = weeklyStatsPatchForDashboard({
          effectiveSpaceId: effectiveWeeklySpaceId,
          storage: storageThisWeek,
          retrieval: retrievalThisWeek,
          hasAnySpace,
        });

        setStats((prev) => ({
          ...prev,
          totalSpaces: totalSpaces,
          ...weeklyPatch,
        }));

        console.log('📊 Dashboard stats calculation:', {
          tierInfo: tierInfo
            ? {
                current_month: tierInfo.api_usage.current_month,
                monthly_requests: tierInfo.limits.monthly_requests,
                tier: tierInfo.current_tier.tier,
              }
            : 'No tier info',
        });

        console.log('📊 Dashboard metrics loaded successfully', {
          cached: !forceRefresh,
          effectiveWeeklySpaceId,
          storageThisWeek,
          retrievalThisWeek,
        });
      } catch (error) {
        console.error('❌ Failed to load dashboard data:', error);
        const idForOuter =
          effectiveWeeklySpaceId ??
          spaceIdForWeekly ??
          currentSpaceIdRef.current ??
          firstAvailableSpaceIdRef.current ??
          null;
        const hasAnySpace =
          availableSpaces.length > 0 ||
          !!currentSpace ||
          !!currentSpaceIdRef.current ||
          !!firstAvailableSpaceIdRef.current;
        const weeklyPatch = weeklyStatsPatchForDashboard({
          effectiveSpaceId: idForOuter,
          storage: idForOuter ? 0 : null,
          retrieval: idForOuter ? 0 : null,
          hasAnySpace,
        });
        setStats((prev) => ({
          ...prev,
          totalSpaces: totalSpaces,
          ...weeklyPatch,
        }));
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
        if (effectiveWeeklySpaceId) {
          previousSpaceIdRef.current = effectiveWeeklySpaceId;
        }
        const runPending = pendingDashboardLoadRef.current;
        pendingDashboardLoadRef.current = false;
        if (runPending) {
          const pendingSpaceId =
            currentSpaceIdRef.current ?? firstAvailableSpaceIdRef.current ?? null;
          queueMicrotask(() => {
            void loadDashboardData(true, pendingSpaceId);
          });
        }
      }
    },
    [availableSpaces, totalSpaces, tierInfo, currentSpace?.space_id, currentSpace, queryClient]
  );

  // When user switches space in header, refresh overview metrics for the selected space
  useEffect(() => {
    const spaceId = currentSpace?.space_id ?? null;
    if (hasLoadedRef.current && spaceId !== null && previousSpaceIdRef.current !== spaceId) {
      void loadDashboardData(false, spaceId);
    }
  }, [currentSpace?.space_id, loadDashboardData]);

  // Consolidated loading logic: Coordinate initial load with SpaceContext
  useEffect(() => {
    // Wait for spaces to finish loading before attempting to load dashboard data
    if (isLoadingSpaces) {
      return;
    }

    const currentSpacesLength = availableSpaces.length;
    const previousSpacesLength = previousSpacesLengthRef.current;

    // Initial load: Only load once when spaces are ready and we haven't loaded yet
    if (!hasLoadedRef.current) {
      // Mark as loaded regardless of spaces count to prevent duplicate loads
      hasLoadedRef.current = true;
      previousSpacesLengthRef.current = currentSpacesLength;
      loadedWithZeroSpacesRef.current = currentSpacesLength === 0;
      
      if (currentSpacesLength > 0) {
        // Spaces are ready, perform initial load (explicit space id avoids weekly=null when currentSpace lags)
        loadDashboardData(false, availableSpaces[0]?.space_id ?? null);
      } else {
        // No spaces available (likely timeout), but loading is complete - stop loading
        setIsLoading(false);
      }
      return;
    }

    // After initial load: Handle space changes
    if (hasLoadedRef.current && previousSpacesLength >= 0) {
      // Case 1: We loaded with 0 spaces (timeout), then spaces actually loaded
      // This is the main issue - we should reload when spaces go from 0 to >0 after timeout
      if (loadedWithZeroSpacesRef.current && previousSpacesLength === 0 && currentSpacesLength > 0) {
        console.log(`🔄 Spaces loaded after timeout (0 → ${currentSpacesLength}), reloading dashboard`);
        loadedWithZeroSpacesRef.current = false; // Clear the flag
        previousSpacesLengthRef.current = currentSpacesLength;
        loadDashboardData(false, availableSpaces[0]?.space_id ?? null);
        return;
      }
      
      // Case 2: Spaces count changed after initial successful load (user created/deleted space)
      // Only reload if spaces genuinely changed (not just initial population)
      if (currentSpacesLength !== previousSpacesLength && previousSpacesLength > 0) {
        console.log(`🔄 Spaces changed from ${previousSpacesLength} to ${currentSpacesLength}, reloading dashboard`);
        previousSpacesLengthRef.current = currentSpacesLength;
        loadDashboardData(false, availableSpaces[0]?.space_id ?? null);
        return;
      }
      
      // Update ref to track current state (even if we don't reload)
      if (currentSpacesLength !== previousSpacesLength) {
        previousSpacesLengthRef.current = currentSpacesLength;
      }
      
      // No reload needed
      return;
    }

    // If no spaces are available and we've already loaded, stop loading
    if (currentSpacesLength === 0 && hasLoadedRef.current) {
      setIsLoading(false);
    }
  }, [availableSpaces, isLoadingSpaces, loadDashboardData]);

  // Refresh when user navigates to dashboard (but only if already loaded once)
  useEffect(() => {
    if (location.pathname === '/dashboard' && hasLoadedRef.current) {
      // Only reload if we've already loaded once (prevents initial double-load)
      loadDashboardData(
        false,
        currentSpace?.space_id ?? availableSpaces[0]?.space_id ?? null
      );
    }
  }, [location.pathname, loadDashboardData, availableSpaces, currentSpace?.space_id]);

  // Reset hasLoadedRef when tierInfo changes (user upgraded, etc.)
  useEffect(() => {
    if (tierInfo && hasLoadedRef.current) {
      // Allow reload when tier info changes (e.g., after upgrade)
      // Reset flag to allow fresh load with new tier data
      hasLoadedRef.current = false;
    }
  }, [tierInfo]);

  // CRITICAL FIX: Force refresh tier info when returning from billing success
  useEffect(() => {
    const checkForTierUpdate = async () => {
      // Check if we're coming back from billing success
      const urlParams = new URLSearchParams(location.search);
      const fromBilling = urlParams.has('billing_success') || 
                          urlParams.has('session_id') || 
                          urlParams.has('upgraded') ||
                          localStorage.getItem('pending_tier_refresh') === 'true';
      
      if (fromBilling) {
        console.log('🔄 Detected return from billing - forcing tier refresh...');

        let checkoutSessionId = urlParams.get('session_id');
        if (!checkoutSessionId && typeof window !== 'undefined') {
          checkoutSessionId = sessionStorage.getItem('memara_pending_checkout_session_id');
        }
        if (checkoutSessionId) {
          try {
            await billingService.confirmCheckoutSession(checkoutSessionId);
          } catch (e) {
            console.warn('confirmCheckoutSession (dashboard) failed:', e);
          } finally {
            try {
              sessionStorage.removeItem('memara_pending_checkout_session_id');
            } catch {
              /* ignore */
            }
          }
        }

        try {
          await billingService.syncSubscriptionFromStripe();
        } catch (e) {
          console.warn('syncSubscriptionFromStripe (dashboard) failed:', e);
        }

        // Force refresh tier information to get updated limits
        await forceRefreshTierInfoRef.current();
        
        // Clear the localStorage flag
        localStorage.removeItem('pending_tier_refresh');
        
        // Clean up URL parameters via React Router so location.search stays in sync
        if (urlParams.has('billing_success') || urlParams.has('session_id') || urlParams.has('upgraded')) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('billing_success');
              next.delete('session_id');
              next.delete('upgraded');
              return next;
            },
            { replace: true }
          );
        }
        
        console.log('✅ Tier refresh completed after billing return');
      }
    };

    // Run the check when component mounts or location changes
    if (location.pathname === '/dashboard') {
      checkForTierUpdate();
    }
  }, [location.pathname, location.search, setSearchParams]);

  // Smart upgrade timing - check if user should see upgrade prompts
  useEffect(() => {
    const checkUpgradeOpportunities = async () => {
      if (!tierInfo) {
        console.log('⏸️ No tier info available, skipping upgrade check');
        return;
      }
      
      console.log(`🔍 Checking upgrade opportunities for tier: ${tierInfo.current_tier.tier}`);
      
      // CRITICAL FIX: Pro and Enterprise users have unlimited spaces/memories - never show limits
      if (tierInfo.current_tier.tier === 'pro' || tierInfo.current_tier.tier === 'enterprise') {
        console.log('✅ User has unlimited tier, no upgrade prompts needed');
        setShouldShowUpgradePrompt(false);
        return;
      }
      
      // Only show upgrade prompts for free and starter tiers
      if (tierInfo.current_tier.tier !== 'free' && tierInfo.current_tier.tier !== 'starter') {
        console.log(`⚠️ Unknown tier: ${tierInfo.current_tier.tier}, skipping checks`);
        return;
      }
      
      const showPrompt = (next: boolean, context: string) => {
        // Identical setState still schedules work while other lanes are pending.
        setShouldShowUpgradePrompt((prev) => (prev === next ? prev : next));
        if (next) {
          setUpgradeContext(context);
        }
      };

      // Check space limits - only show warning if user has exactly reached the limit
      const spaceLimit = await getResourceLimitsRef.current('spaces', availableSpaces.length);
      if (spaceLimit && spaceLimit.limit_status.percentage_used >= 100) {
        console.log(`🚨 Space limit reached: ${availableSpaces.length}/${spaceLimit.limit_status.limit}`);
        showPrompt(true, `You've reached your space limit of ${spaceLimit.limit_status.limit} spaces`);
        return;
      }
      
      // Check memory limits if we have memory count - only show warning at 90%+
      if (stats.totalMemories > 0) {
        const memoryLimit = await getResourceLimitsRef.current('memories', stats.totalMemories);
        if (memoryLimit && memoryLimit.limit_status.percentage_used >= 90) {
          console.log(`⚠️ Memory limit at ${memoryLimit.limit_status.percentage_used}%`);
          showPrompt(true, `You're using ${memoryLimit.limit_status.percentage_used}% of your memory limit`);
          return;
        }
      }
      
      // Reset upgrade prompt if not at limits
      console.log('✅ No upgrade prompts needed - under limits');
      showPrompt(false, '');
    };
    
    if (tierInfo && stats.totalMemories >= 0) {
      checkUpgradeOpportunities();
    }
    // Read getResourceLimits through a ref. An inline callback changed every
    // render, re-ran this effect, and the setState never went idle.
  }, [tierInfo, availableSpaces.length, stats.totalMemories]);

  // Update stats when spaces are loaded from SpaceContext
  useEffect(() => {
    if (!isLoadingSpaces) {
      setStats((prevStats: DashboardStats) => ({
        ...prevStats,
        totalSpaces: totalSpaces
      }));
    }
  }, [totalSpaces, isLoadingSpaces]);

  // Format relative time for activity display
  const formatRelativeTime = useCallback((timestamp: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - timestamp.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }
    
    return timestamp.toLocaleDateString();
  }, []);

  // Get activity icon based on type
  const getActivityIcon = useCallback((type: string) => {
    switch (type) {
      case 'memory_created':
        return <Plus className="w-4 h-4" />;
      case 'memory_updated':
        return <Zap className="w-4 h-4" />;
      case 'memory_deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'search_performed':
        return <Search className="w-4 h-4" />;
      case 'memory_listed':
      case 'memory_fetched':
        return <Search className="w-4 h-4" />;
      case 'space_created':
        return <Target className="w-4 h-4" />;
      case 'achievement_unlocked':
      case 'milestone_reached':
        return <Trophy className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  }, []);

  // Transform ActivityEvent to dashboard format (collapsed: 3, expanded: up to 20)
  const recentActivities = useMemo(() => {
    if (isLoadingActivities) {
      return [];
    }

    const cap = showRecentActivityExpanded ? 20 : 3;
    return activityTimelineActivities.slice(0, cap).map((activity) => ({
      type: activity.type,
      action: activity.title,
      time: formatRelativeTime(activity.timestamp),
      icon: getActivityIcon(activity.type),
    }));
  }, [
    activityTimelineActivities,
    isLoadingActivities,
    formatRelativeTime,
    getActivityIcon,
    showRecentActivityExpanded,
  ]);

  const handleSelectPlan = async (planId: string, billingCycle: 'monthly' | 'annual' = 'monthly', couponCode?: string) => {
    try {
      if (planId === 'enterprise') {
        billingService.contactEnterpriseSales();
      } else if (planId !== 'free') {
        await billingService.upgradeTier(planId, billingCycle, couponCode);
      }
      setShowPricingModal(false);
    } catch (error) {
      console.error('Error upgrading plan:', error);
      toast.error(t('billing.checkoutErrorTitle', { ns: 'pricing' }), {
        description: extractErrorMessage(
          error,
          t('billing.checkoutErrorDescription', { ns: 'pricing' })
        ),
      });
    }
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={t('loading.dashboard', { ns: 'dashboard' }) as string}
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" aria-hidden />
          <span className="text-lg text-muted-foreground">{t('loading.dashboard', { ns: 'dashboard' })}</span>
        </div>
      </div>
    );
  }

  // Advanced tools data with click handlers
  const handleAdvancedToolClick = (toolId: string) => {
    switch (toolId) {
      case 'analytics':
        setSearchParams({ view: 'analytics' });
        break;
      case 'search-insights':
        // Navigate to analytics page with search insights focus
        setSearchParams({ view: 'analytics' });
        // Scroll to search insights section after navigation
        setTimeout(() => {
          const searchInsightsElement = document.querySelector('[data-section="search-insights"]');
          if (searchInsightsElement) {
            searchInsightsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
        break;
      case 'activity':
        // Navigate to analytics page with activity timeline focus
        setSearchParams({ view: 'analytics' });
        // Scroll to activity timeline section after navigation
        setTimeout(() => {
          const activityTimelineElement = document.querySelector('[data-section="activity-timeline"]');
          if (activityTimelineElement) {
            activityTimelineElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
        break;
      case 'settings':
        navigate('/settings/profile');
        break;
      default:
        break;
    }
  };

  const welcomeName =
    user?.user_metadata?.full_name?.split(' ')[0] ||
    user?.user_metadata?.name?.split(' ')[0] ||
    user?.email?.split('@')[0] ||
    (t('welcomeUserFallback', { ns: 'dashboard' }) as string);

  const advancedTools = [
    {
      title: t('advancedDirectory.productivity.title', { ns: 'dashboard' }),
      description: t('advancedDirectory.productivity.description', { ns: 'dashboard' }),
      icon: <TrendingUp className="w-4 h-4" aria-hidden />,
      href: '#analytics',
      onClick: () => handleAdvancedToolClick('analytics')
    },
    {
      title: t('advancedDirectory.searchInsights.title', { ns: 'dashboard' }),
      description: t('advancedDirectory.searchInsights.description', { ns: 'dashboard' }),
      icon: <Search className="w-4 h-4" aria-hidden />,
      href: '#search-insights',
      onClick: () => handleAdvancedToolClick('search-insights')
    },
    {
      title: t('advancedDirectory.activity.title', { ns: 'dashboard' }),
      description: t('advancedDirectory.activity.description', { ns: 'dashboard' }),
      icon: <Clock className="w-4 h-4" aria-hidden />,
      href: '#activity',
      onClick: () => handleAdvancedToolClick('activity')
    },
    {
      title: t('advancedDirectory.settings.title', { ns: 'dashboard' }),
      description: t('advancedDirectory.settings.description', { ns: 'dashboard' }),
      icon: <Settings className="w-4 h-4" aria-hidden />,
      href: '#settings',
      onClick: () => handleAdvancedToolClick('settings')
    },
  ];

  return (
    <Tabs
      value={activeView}
      onValueChange={(value) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('view', value);
          return next;
        });
      }}
      className="min-h-screen bg-background"
    >
      {/* Header - Tab list for view switcher (accessible) */}
      <header className="border-b bg-card/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <TabsList className="flex flex-shrink-0 overflow-x-auto">
              <TabsTrigger value="overview">{t('tabs.overview', { ns: 'dashboard' })}</TabsTrigger>
              <TabsTrigger value="analytics">{t('tabs.analytics', { ns: 'dashboard' })}</TabsTrigger>
              <TabsTrigger value="integrations">{t('tabs.integrations', { ns: 'dashboard' })}</TabsTrigger>
              <TabsTrigger value="monitoring">{t('tabs.monitoring', { ns: 'dashboard' })}</TabsTrigger>
            </TabsList>
            <PortalButton
              className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white border-0 shadow-lg hover:shadow-xl hover:shadow-indigo-500/50 transition-all duration-300 flex-shrink-0"
            >
              {t('buttons.memoryWorkspace', { ns: 'navigation' })}
            </PortalButton>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {showPostLoginHint && (
          <Alert className="flex items-center justify-between gap-4">
            <AlertDescription className="flex-1">
              {t('postLoginHint.message', { ns: 'dashboard' })}
            </AlertDescription>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" variant="secondary" asChild>
                <Link to="/spaces">{t('postLoginHint.createSpace', { ns: 'dashboard' })}</Link>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPostLoginHint(false)} aria-label={t('postLoginHint.dismiss', { ns: 'dashboard' }) as string}>
                {t('postLoginHint.dismiss', { ns: 'dashboard' })}
              </Button>
            </div>
          </Alert>
        )}
        <TabsContent value="analytics" className="mt-0">
          <AnalyticsPage />
        </TabsContent>
        <TabsContent value="monitoring" className="mt-0">
          <MonitoringPage />
        </TabsContent>
        <TabsContent value="integrations" className="mt-0">
          <IntegrationsPage />
        </TabsContent>
        <TabsContent value="overview" className="mt-0">
          <div className="space-y-8">
            {!isLoadingSpaces && spacesError && availableSpaces.length === 0 && (
              <section
                role="alert"
                data-testid="spaces-load-error"
                className="mb-6"
              >
                <Card className="border-destructive/40 bg-destructive/5">
                  <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground mb-1">
                        {t('spacesLoadError.title', { ns: 'dashboard' })}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {t('spacesLoadError.description', { ns: 'dashboard' })}
                      </p>
                    </div>
                    <Button
                      type="button"
                      className="flex-shrink-0 min-h-[44px]"
                      onClick={() => {
                        void refreshSpaces();
                      }}
                    >
                      {t('retry', { ns: 'common' })}
                    </Button>
                  </CardContent>
                </Card>
              </section>
            )}
            {/* Empty state only after a successful list that contained no spaces */}
            {!isLoadingSpaces && !spacesError && totalSpaces === 0 && (
              <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-6"
                data-testid="dashboard-no-spaces"
              >
                <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                  <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground mb-1">
                        {t('noSpaces.title', { ns: 'dashboard' })}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {t('noSpaces.description', { ns: 'dashboard' })}
                      </p>
                    </div>
                    <Button asChild className="flex-shrink-0 min-h-[44px]">
                      <Link to="/spaces">{t('noSpaces.createSpace', { ns: 'dashboard' })}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.section>
            )}
            {/* Section 1: Welcome + Key Metrics */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  👋 {t('welcomeBack', { ns: 'dashboard', name: welcomeName })}
                </h1>
                {currentSpace && (
                  <p className="text-muted-foreground text-sm">
                    {t('weeklyActivityWindowCaption', { ns: 'dashboard' })}
                  </p>
                )}
              </div>
                
              {/* Key metrics: weekly Storage and Retrieval */}
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-6 md:items-stretch"
                data-testid="weekly-activity-metrics"
              >
                <Card className="h-full bg-gradient-to-r from-slate-50 to-zinc-50 border-slate-200">
                  <CardContent className="p-6 h-full flex flex-col justify-center">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {t('stats.storageThisWeek', { ns: 'dashboard' })}
                        </p>
                        <p className="text-3xl font-bold text-foreground tabular-nums">
                          {currentSpace && stats.storageThisWeek !== null
                            ? stats.storageThisWeek
                            : '\u2014'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 min-h-[1.25rem]">
                          {t('stats.storageThisWeekCaption', { ns: 'dashboard' })}
                        </p>
                      </div>
                      <Layers className="w-10 h-10 shrink-0 text-slate-600" aria-hidden />
                    </div>
                  </CardContent>
                </Card>

                <Card className="h-full bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
                  <CardContent className="p-6 h-full flex flex-col justify-center">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {t('stats.retrievalThisWeek', { ns: 'dashboard' })}
                        </p>
                        <p className="text-3xl font-bold text-foreground tabular-nums">
                          {currentSpace && stats.retrievalThisWeek !== null
                            ? stats.retrievalThisWeek
                            : '\u2014'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 min-h-[1.25rem]">
                          {t('stats.retrievalThisWeekCaption', { ns: 'dashboard' })}
                        </p>
                      </div>
                      <Search className="w-10 h-10 shrink-0 text-violet-600" aria-hidden />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Getting Started for New Users */}
              {currentSpace && stats.totalMemories === 0 && (
                <Card className="mt-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-orange-800 mb-1">
                          {t('gettingStartedCallout.title', { ns: 'dashboard' })}
                        </h3>
                        <p className="text-sm text-orange-700">
                          {t('gettingStartedCallout.description', { ns: 'dashboard' })}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-orange-300 text-orange-700 hover:bg-orange-100"
                          onClick={enterWorkspace}
                        >
                          {t('quickActions.createMemory', { ns: 'dashboard' })}
                        </Button>
                        <Button
                          size="sm"
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={() => setSearchParams({ view: 'integrations' })}
                        >
                          {t('quickActions.addIntegration', { ns: 'dashboard' })}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.section>

            {/* Recent Activity (collapsed by default) */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="border-border bg-card/60 backdrop-blur-sm">
                {!showRecentActivityExpanded ? (
                  <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                    <div className="space-y-1 min-w-0">
                      <CardTitle className="text-xl font-semibold leading-tight">
                        🕒 {t('stats.recentActivity', { ns: 'dashboard' })}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {activityTimelineActivities.length > 0
                          ? t('activityTimeline.recentCount', {
                              ns: 'dashboard',
                              count: activityTimelineActivities.length,
                            })
                          : t('activityTimeline.noRecentActivity', { ns: 'dashboard' })}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowRecentActivityExpanded(true)}
                      >
                        {t('activityTimeline.showMore', { ns: 'dashboard' })}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSearchParams({ view: 'analytics' })}
                      >
                        {t('tabs.analytics', { ns: 'dashboard' })}
                      </Button>
                    </div>
                  </CardHeader>
                ) : (
                  <>
                    <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 pb-2">
                      <CardTitle className="text-xl font-semibold leading-tight">
                        🕒 {t('stats.recentActivity', { ns: 'dashboard' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-4">
                        {recentActivities.map((activity, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                            <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                              {activity.icon}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground">{activity.action}</p>
                              <p className="text-xs text-muted-foreground">{activity.time}</p>
                            </div>
                          </div>
                        ))}
                        {recentActivities.length === 0 && (
                          <div className="text-center py-6">
                            <p className="text-sm text-muted-foreground">{t('activityTimeline.noRecentActivity', { ns: 'dashboard' })}</p>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 flex flex-wrap justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowRecentActivityExpanded(false)}>
                          {t('activityTimeline.collapse', { ns: 'dashboard' })}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setSearchParams({ view: 'analytics' })}>
                          {t('tabs.analytics', { ns: 'dashboard' })}
                        </Button>
                      </div>
                    </CardContent>
                  </>
                )}
              </Card>
            </motion.section>

            {/* Section 4: Advanced Tools (Collapsible) */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card className="border-border bg-muted/20">
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 shrink-0" aria-hidden />
                      <span>{t('advancedToolsCard.title', { ns: 'dashboard' })}</span>
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 self-start sm:self-center"
                      onClick={() => setShowAdvancedTools(!showAdvancedTools)}
                    >
                      {showAdvancedTools ? (
                        <ChevronUp className="w-4 h-4 mr-2" aria-hidden />
                      ) : (
                        <ChevronDown className="w-4 h-4 mr-2" aria-hidden />
                      )}
                      {showAdvancedTools
                        ? t('advancedToolsCard.collapse', { ns: 'dashboard' })
                        : t('advancedToolsCard.expand', { ns: 'dashboard' })}
                    </Button>
                  </div>
                </CardHeader>
                {showAdvancedTools && (
                  <CardContent className="pt-0">
                    <DirectoryList
                      items={advancedTools}
                      columns={2}
                      className="mt-4"
                    />
                  </CardContent>
                )}
              </Card>
            </motion.section>
          </div>
        </TabsContent>
      </main>

      {showPricingModal && (
        <PricingModal
          isOpen={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          onSelectPlan={handleSelectPlan}
        />
      )}
    </Tabs>
  );
}
